import { memo, useCallback, useState } from "react";
import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";
import LoginWelcomeBody from "../../components/Login/LoginWelcomeBody";
import RectangleButton from "../../components/Buttons/RectangleButton";
import CustomCheckbox from "../../components/CustomCheckbox";
import { MARKIT_DOCS } from "@markit/common.utils";
import { OnboardingFlowType } from "@markit/common.types";
import { isDesktop } from "react-device-detect";
import { ContainerButton } from "../../components/Buttons/ContainerButton";
import { LightTheme } from "../../hooks/useTheme";
import AlertContainer from "../../components/Containers/AlertContainer";
import { CUSTOMIZE_EXPERIENCE_GOALS } from "../../utils/onboardingFlowUtils";

type LoginOnboardingCustomizeExperienceProps = {
  continueOnPress: (
    selectedGoals: string[],
    selectedTypes: OnboardingFlowType[]
  ) => Promise<void>;
  backOnPress: () => void;
};

const LoginOnboardingCustomizeExperience = (
  props: LoginOnboardingCustomizeExperienceProps
) => {
  const { continueOnPress, backOnPress } = props;
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleGoal = useCallback((goal: string) => {
    setSelectedGoals((prev) =>
      prev.includes(goal) ? prev.filter((g) => g !== goal) : [...prev, goal]
    );
  }, []);

  const finalizeContinueOnPress = useCallback(async () => {
    if (!selectedGoals.length) {
      setShowAlert(true);
      return;
    }
    setLoading(true);
    const selectedTypes = CUSTOMIZE_EXPERIENCE_GOALS.filter((goal) =>
      selectedGoals.includes(goal.title)
    ).map((goal) => goal.type);
    await continueOnPress(selectedGoals, selectedTypes);
    setLoading(false);
  }, [selectedGoals, continueOnPress]);

  return (
    <>
      <div
        style={
          isDesktop
            ? { position: "absolute", left: 20, top: 20 }
            : { display: "flex", paddingLeft: 14 }
        }
      >
        <ContainerButton onPress={backOnPress} theme={LightTheme} />
      </div>
      <LoginWelcomeBody
        title="Customize Your Experience"
        subtext="Help us cater your experience to fit what you're looking for."
      >
        <div className="ColumnNormal" style={{ gap: 7, width: "100%" }}>
          <span className="bodySmallMedium">What are you here to do?</span>
          <div className="ColumnNormal" style={{ gap: 24, width: "100%" }}>
            {CUSTOMIZE_EXPERIENCE_GOALS.map((goal) => {
              const isSelected = selectedGoals.includes(goal.title);
              return (
                <div
                  key={goal.title}
                  className="AlignedRowSpaced"
                  style={{
                    border: `1px solid ${Colors.GRAY2}`,
                    borderRadius: 12,
                    padding: 14,
                    cursor: "pointer",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                  onClick={() => toggleGoal(goal.title)}
                >
                  <div className="AlignedRow" style={{ gap: 4 }}>
                    <span className="bodyMedium">{goal.title}</span>
                    <Icon
                      icon="mdi:information-outline"
                      height={14}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(MARKIT_DOCS + goal.url, "_blank");
                      }}
                      style={{
                        color: Colors.BLACK,
                        cursor: "pointer",
                        marginBottom: -2,
                      }}
                    />
                  </div>
                  <CustomCheckbox
                    checked={isSelected}
                    sx={{ padding: 0 }}
                    altColor={Colors.BLACK}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <div
          className="ColumnNormal"
          style={{ gap: 24, width: "100%", paddingBottom: 100 }}
        >
          <RectangleButton
            buttonLabel="Continue"
            onPress={finalizeContinueOnPress}
            altColor={Colors.BLACK}
            altTextColor={Colors.WHITE}
            loading={loading}
            altPaddingVert={14}
          />
        </div>
      </LoginWelcomeBody>
      {showAlert ? (
        <AlertContainer
          headerComp="Please select at least one option to continue."
          subHeaderComp="This will help us better tailor your experience for you."
          closeModal={() => setShowAlert(false)}
          closeOnOutsidePress={true}
          hideModal={!showAlert}
        />
      ) : null}
    </>
  );
};

export default memo(LoginOnboardingCustomizeExperience);
